import * as Diff from "diff";

export type LineType = "context" | "delete" | "insert";

export interface DiffLine {
  type: LineType;
  content: string;
  marker: " " | "-" | "+";
  lineNumber?: {
    old?: number;
    new?: number;
  };
}

export interface Hunk {
  lines: DiffLine[];
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
}

export interface DiffResult {
  hunks: Hunk[];
  hunkCount: number;
  hasChanges: boolean;
}

/**
 * テキストを行差分用に正規化
 * - EOL統一
 * - 文字列中のリテラル改行(\n, \r\n)を実改行へ展開
 * - 見出しや箇条書きなどの前後に改行を挿入して差分の可読性を向上
 * - 末尾改行を付与
 */
function normalizeForLineDiff(text: string): string {
  let s = text ?? "";
  // EOL統一
  s = s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  // リテラル改行を展開
  s = s.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
  // 差分の視認性向上のための改行挿入
  s = insertNewlinesForDiff(s);
  // 末尾に改行がない場合は追加
  if (s.length > 0 && !s.endsWith("\n")) s += "\n";
  return s;
}

// 差分抽出のための補助: ヘッダや箇条書きの区切りを明確化
function insertNewlinesForDiff(src: string): string {
  let s = src;
  // 見出し(##, ###, ####...)の直前に改行
  s = s.replace(/([^\n])\s*(##+\s)/g, "$1\n$2");
  // 複数空白の後に続く "- " を行頭に寄せる
  s = s.replace(/[\t ]{2,}-\s/g, "\n- ");
  // 日本語句点の直後に続くハイフン箇条書きは改行
  s = s.replace(/。\s*-\s/g, "。\n- ");
  return s;
}

/**
 * diff-libraryのハンクからDiffLine配列を構築
 */
function buildDiffLines(hunk: {
  lines: string[];
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
}): DiffLine[] {
  const lines = hunk.lines;
  const out: DiffLine[] = [];
  let oldLineNum = hunk.oldStart;
  let newLineNum = hunk.newStart;

  for (const raw of lines) {
    // "\ No newline at end of file"のような特殊マーカーをスキップ
    if (/^\\ No newline at end of file/.test(raw)) {
      continue;
    }

    const marker = (raw[0] as " " | "-" | "+") ?? " ";
    const content = raw.slice(1);

    if (marker === " ") {
      out.push({
        type: "context",
        marker,
        content,
        lineNumber: {
          old: oldLineNum,
          new: newLineNum,
        },
      });
      oldLineNum++;
      newLineNum++;
    } else if (marker === "-") {
      out.push({
        type: "delete",
        marker,
        content,
        lineNumber: {
          old: oldLineNum,
        },
      });
      oldLineNum++;
    } else if (marker === "+") {
      out.push({
        type: "insert",
        marker,
        content,
        lineNumber: {
          new: newLineNum,
        },
      });
      newLineNum++;
    } else {
      // 想定外は文脈として扱う
      out.push({
        type: "context",
        marker: " ",
        content: raw,
        lineNumber: {
          old: oldLineNum,
          new: newLineNum,
        },
      });
      oldLineNum++;
      newLineNum++;
    }
  }

  return out;
}

/**
 * 2つのテキストから行単位の差分を生成
 * @param oldText 更新前のテキスト
 * @param newText 更新後のテキスト
 * @param contextLines 変更箇所の前後に含める文脈行数（デフォルト: 3）
 */
export function createLineDiff(
  oldText: string,
  newText: string,
  contextLines: number = 3,
): DiffResult {
  const before = normalizeForLineDiff(oldText);
  const after = normalizeForLineDiff(newText);

  // 差分がない場合の早期リターン
  if (before === after) {
    return {
      hunks: [],
      hunkCount: 0,
      hasChanges: false,
    };
  }

  // ハンク抽出（変更点とその周辺の文脈を含む）
  const patchHunks = Diff.structuredPatch("before", "after", before, after, "", "", {
    context: contextLines,
  });

  const hunksRaw = patchHunks.hunks;
  const hunks: Hunk[] = hunksRaw.map((h) => ({
    lines: buildDiffLines(h),
    oldStart: h.oldStart,
    oldLines: h.oldLines,
    newStart: h.newStart,
    newLines: h.newLines,
  }));

  return {
    hunks,
    hunkCount: hunks.length,
    hasChanges: hunks.length > 0,
  };
}

/**
 * 完全なドキュメントビューと個別差分制御を組み合わせた差分を生成
 */
export function createFullDocumentDiff(oldText: string, newText: string): DiffResult {
  const before = normalizeForLineDiff(oldText);
  const after = normalizeForLineDiff(newText);

  if (before === after) {
    return {
      hunks: [],
      hunkCount: 0,
      hasChanges: false,
    };
  }

  const beforeLines = before.split("\n");

  // 個別の変更箇所を検出（contextLines=0）
  const individualPatch = Diff.structuredPatch("before", "after", before, after, "", "", {
    context: 0,
  });

  // ドキュメント全体を表示するため、欠落部分を埋める
  const fullHunks: Hunk[] = [];
  let currentOldLine = 1;
  let currentNewLine = 1;

  for (let i = 0; i < individualPatch.hunks.length; i++) {
    const hunk = individualPatch.hunks[i];
    if (!hunk) continue;

    // 前のhunkとの間に欠落がある場合、contextとして追加
    if (currentOldLine < hunk.oldStart) {
      const gapLines: DiffLine[] = [];
      for (let lineNum = currentOldLine; lineNum < hunk.oldStart; lineNum++) {
        const content = beforeLines[lineNum - 1] || "";
        gapLines.push({
          type: "context",
          marker: " ",
          content,
          lineNumber: {
            old: lineNum,
            new: currentNewLine + (lineNum - currentOldLine),
          },
        });
      }

      if (gapLines.length > 0) {
        fullHunks.push({
          lines: gapLines,
          oldStart: currentOldLine,
          oldLines: gapLines.length,
          newStart: currentNewLine,
          newLines: gapLines.length,
        });
      }

      const gapSize = hunk.oldStart - currentOldLine;
      currentOldLine = hunk.oldStart;
      currentNewLine += gapSize;
    }

    // 実際の変更hunkを追加
    fullHunks.push({
      lines: buildDiffLines(hunk),
      oldStart: hunk.oldStart,
      oldLines: hunk.oldLines,
      newStart: hunk.newStart,
      newLines: hunk.newLines,
    });

    currentOldLine = hunk.oldStart + hunk.oldLines;
    currentNewLine = hunk.newStart + hunk.newLines;
  }

  // 最後のhunk以降に残りの行があれば追加
  if (currentOldLine <= beforeLines.length) {
    const remainingLines: DiffLine[] = [];
    for (let lineNum = currentOldLine; lineNum <= beforeLines.length; lineNum++) {
      const content = beforeLines[lineNum - 1] || "";
      remainingLines.push({
        type: "context",
        marker: " ",
        content,
        lineNumber: {
          old: lineNum,
          new: currentNewLine + (lineNum - currentOldLine),
        },
      });
    }

    if (remainingLines.length > 0) {
      fullHunks.push({
        lines: remainingLines,
        oldStart: currentOldLine,
        oldLines: remainingLines.length,
        newStart: currentNewLine,
        newLines: remainingLines.length,
      });
    }
  }

  return {
    hunks: fullHunks,
    hunkCount: fullHunks.length,
    hasChanges: individualPatch.hunks.length > 0,
  };
}

/**
 * 差分の統計情報を計算
 */
export function calculateDiffStats(diff: DiffResult): {
  additions: number;
  deletions: number;
  modifications: number;
} {
  let additions = 0;
  let deletions = 0;

  for (const hunk of diff.hunks) {
    for (const line of hunk.lines) {
      if (line.type === "insert") additions++;
      if (line.type === "delete") deletions++;
    }
  }

  // 修正は削除と挿入がペアになっている場合（簡易計算）
  const modifications = Math.min(additions, deletions);

  return {
    additions: additions - modifications,
    deletions: deletions - modifications,
    modifications,
  };
}
